import { ArrowUp, ArrowDown, Activity, Minus, Calendar } from "lucide-react";
import React from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";

const APEmissionCard = ({ emissionData }) => {
  const data = emissionData?.data || emissionData || {};
  
  console.log("EmissionCard Data:", data);

  const beforeValue = Math.round(data.before_additive * 100) / 100 || 0;
  const afterValue = Math.round(data.after_additive * 100) / 100 || 0;
  const percentageChange = data.percentage_change || 0;

  const beforeStart = data.date_ranges?.before_period?.start ? 
    new Date(data.date_ranges.before_period.start) : new Date();
  const beforeEnd = data.date_ranges?.before_period?.end ? 
    new Date(data.date_ranges.before_period.end) : new Date();
  const afterStart = data.date_ranges?.after_period?.start ? 
    new Date(data.date_ranges.after_period.start) : new Date();
  const afterEnd = data.date_ranges?.after_period?.end ? 
    new Date(data.date_ranges.after_period.end) : new Date();

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getDatePosition = (date, startDate, endDate) => {
    const totalDuration = endDate.getTime() - startDate.getTime();
    const positionFromStart = date.getTime() - startDate.getTime();
    return (positionFromStart / totalDuration) * 100;
  };

  const chartHeight = 100;
  const maxValue = Math.max(beforeValue, afterValue, 1) * 1.2;
  const beforeHeight = (beforeValue / maxValue) * chartHeight;
  const afterHeight = (afterValue / maxValue) * chartHeight;

  const areaPath = `M 0 100 
                   L 0 ${100 - beforeHeight} 
                   L 100 ${100 - afterHeight} 
                   L 100 100 Z`;

  return (
    <div className="flex flex-col h-full p-4 bg-gradient-to-br from-white to-slate-50 rounded-xl shadow-sm border border-slate-200">
      {/* Header */}
      <div className="flex items-center mb-4">
        <div className="p-2 bg-[#1a53761a] rounded-lg mr-3">
          <Activity size={18} className="text-[#a93226]" />
        </div>
        <div>
          <h3 className="font-bold text-slate-800">Emissions</h3>
          <p className="text-xs text-slate-500">
            CO₂ Emissions Comparison
          </p>
        </div>
      </div>

      {/* Timeline Information */}
      {/* <div className="mb-4 p-3 bg-slate-50 rounded-lg">
        <div className="flex items-center text-xs text-slate-600 mb-2">
          <Calendar size={12} className="mr-1" />
          <span>Comparison Timeline</span>
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="text-center p-2 bg-blue-50 rounded">
            <div className="font-semibold text-blue-800">Before</div>
            <div className="text-blue-600">
              {formatDate(beforeStart)} - {formatDate(beforeEnd)}
            </div>
          </div>
          <div className="text-center p-2 bg-green-50 rounded">
            <div className="font-semibold text-green-800">After</div>
            <div className="text-green-600">
              {formatDate(afterStart)} - {formatDate(afterEnd)}
            </div>
          </div>
        </div>
      </div> */}

      {/* Area Chart */}
      <div className="flex-1 mb-5">
        <div className="relative h-32">
          {/* Y-axis */}
          <div className="absolute left-0 top-0 bottom-6 flex flex-col justify-between text-xs text-slate-500 w-8">
            <span>{Math.round(maxValue)} MT</span>
            <span>{Math.round(maxValue / 2)} MT</span>
            <span>0 MT</span>
          </div>

          {/* Chart Container */}
          <div className="ml-8 h-full relative">
            {/* Horizontal grid lines */}
            <div className="absolute inset-0 flex flex-col justify-between">
              {[0, 0.25, 0.5, 0.75, 1].map((_, i) => (
                <div key={i} className="h-px bg-slate-200"></div>
              ))}
            </div>

            {/* Timeline Labels */}
            <div className="absolute left-0 right-0 bottom-0 flex justify-between text-xs text-slate-500 h-0 items-start pt-1">
              <span>Before</span>
              <span>After</span>
            </div>

            {/* Vertical grid lines */}
            <div className="absolute inset-0 flex justify-between">
              <div className="w-px bg-slate-200"></div>
              <div className="w-px bg-slate-200"></div>
            </div>

            {/* SVG Area */}
            <svg
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              className="w-full h-full"
            >
              <defs>
                <linearGradient
                  id="areaGradient"
                  x1="0%"
                  y1="0%"
                  x2="0%"
                  y2="100%"
                >
                  <stop offset="0%" stopColor="#27ae60" stopOpacity="0.3" />
                  <stop offset="100%" stopColor="#27ae60" stopOpacity="0.1" />
                </linearGradient>
              </defs>

              <path d={areaPath} fill="url(#areaGradient)" />
              <path
                d={`M 0 ${100 - beforeHeight} L 100 ${100 - afterHeight}`}
                stroke="#27ae60"
                strokeWidth="2"
                fill="none"
                strokeLinecap="round"
              />
            </svg>

            {/* Value Labels */}
            <div
              className="absolute text-xs font-semibold bg-white px-2 py-1 rounded border border-slate-200 shadow-sm -translate-x-1/2"
              style={{
                left: '0%',
                top: `${100 - beforeHeight + 10}%`,
              }}
            >
              {beforeValue?.toFixed(2)} MT
            </div>
            <div
              className="absolute text-xs font-semibold bg-white px-2 py-1 rounded border border-slate-200 shadow-sm -translate-x-1/2"
              style={{
                left: '100%',
                top: `${100 - afterHeight + 10}%`,
              }}
            >
              {afterValue?.toFixed(2)} MT
            </div>

            {/* Markers */}
            <div
              className="absolute w-3 h-3 bg-[#a93226] rounded-full border-2 border-white shadow-sm -translate-x-1.5 -translate-y-1.5"
              style={{
                left: '0%',
                top: `${100 - beforeHeight}%`,
              }}
            />
            <div
              className="absolute w-3 h-3 bg-[#27ae60] rounded-full border-2 border-white shadow-sm -translate-x-1.5 -translate-y-1.5"
              style={{
                left: '100%',
                top: `${100 - afterHeight}%`,
              }}
            />
          </div>
        </div>
      </div>

      {/* Statistics */}
      {/* <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="text-center p-2 bg-blue-50 rounded-lg">
          <div className="text-xs text-blue-600 mb-1">Before</div>
          <div className="font-bold text-blue-800">{beforeValue} MT</div>
          <div className="text-xs text-blue-500">
            {data.record_counts?.before_period || 0} records
          </div>
        </div>
        <div className="text-center p-2 bg-green-50 rounded-lg">
          <div className="text-xs text-green-600 mb-1">After</div>
          <div className="font-bold text-green-800">{afterValue} MT</div>
          <div className="text-xs text-green-500">
            {data.record_counts?.after_period || 0} records
          </div>
        </div>
      </div> */}

      {/* Percentage Change Indicator */}
      <div className="pt-3 border-t border-slate-100">
        <div
          className={`flex items-center justify-center gap-1 p-3 rounded-xl ${
            percentageChange < 0 ? "bg-[#27ae601a]" : "bg-[#a932261a]"
          }`}
        >
          {percentageChange < 0 ? (
            <ArrowDown size={20} className="text-[#27ae60]" />
          ) : percentageChange > 0 ? (
            <ArrowUp size={20} className="text-[#a93226]" />
          ) : (
            <Minus size={16} className="text-slate-400" />
          )}
          <span
            className={`text-lg font-bold ${
              percentageChange < 0
                ? "text-[#27ae60]"
                : percentageChange > 0
                ? "text-[#a93226]"
                : "text-slate-500"
            }`}
          >
            {Math.abs(percentageChange).toFixed(1)}%{" "}
            {percentageChange < 0
              ? "decrease"
              : percentageChange > 0
              ? "increase"
              : "no change"}
          </span>
        </div>
      </div>

      {/* Data Quality Note */}
      {/* {data.data_quality?.note && (
        <div className="mt-3 p-2 bg-amber-50 rounded text-xs text-amber-700">
          {data.data_quality.note}
        </div>
      )} */}
    </div>
  );
};

export default APEmissionCard;